// Alert: a condition that stays on the page until it changes. An error with a way out, a
// caution to read first, a result, a note. The tone is a tint over whatever step it sits on,
// with an icon whose shape says the same, so colour never carries it alone. The four tones are
// shared with the toast (ui/Toast). docs/operations/design-standard.md, "Feedback".

import React from 'react';
import { Button, IconButton } from './Button';
import Reveal from './Reveal';
import Tooltip from './Tooltip';
import { IconCheck, IconClose, IconError, IconInfo, IconWarning, type IconProps } from './icons';

export type Tone = 'error' | 'warning' | 'success' | 'info';

/** Per tone: the tint laid over the surface, the ink for the icon (and for bare text), the icon. */
export const TONE: Record<Tone, { wash: string; ink: string; Icon: React.FC<IconProps> }> = {
  error: { wash: 'bg-red-500/10', ink: 'text-red-300', Icon: IconError },
  warning: { wash: 'bg-amber-500/10', ink: 'text-amber-300', Icon: IconWarning },
  success: { wash: 'bg-green-500/10', ink: 'text-green-300', Icon: IconCheck },
  info: { wash: 'bg-sky-500/10', ink: 'text-sky-300', Icon: IconInfo },
};

export type AlertVariant = 'banner' | 'note' | 'text';

export interface AlertProps {
  tone?: Tone;
  /**
   * `banner` (default): a block over a page or a panel, with room for a title, actions and a
   * dismiss. `note`: a compact tinted line inside a card or under a field. `text`: no fill,
   * the words in the tone's ink, for the line under a form or an action that failed.
   * `title`, `action`, `onRetry` and `onDismiss` are drawn on all three.
   */
  variant?: AlertVariant;
  /** One line naming what is wrong here. The body says what to do about it. */
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Keys that resolve it (kit Buttons), at the right on a banner, after the words on a note. */
  action?: React.ReactNode;
  /** Draws a tonal "Try again" key: the error with a way out. */
  onRetry?: () => void;
  retryLabel?: string;
  /** The retry key's tooltip, in the operator's words. */
  retryTip?: string;
  /** A key the reader can put the alert away with, drawn on every variant. Leave it out for a standing condition. */
  onDismiss?: () => void;
  dismissLabel?: string;
  /**
   * How a screen reader hears it arrive: `alert` interrupts, `status` waits its turn, `false`
   * is a standing note read in place. Errors interrupt by default, the rest wait.
   */
  live?: 'alert' | 'status' | false;
  /** When given, the alert grows in and dissolves out on its own instead of popping. */
  show?: boolean;
  /** Replaces the tone's icon; `false` leaves it out. */
  icon?: React.ReactNode | false;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  tone = 'error', variant = 'banner', title, children, action, onRetry, retryLabel = 'Try again',
  retryTip = 'Load this again', onDismiss, dismissLabel = 'Dismiss', live, show, icon, className = '',
}) => {
  const t = TONE[tone];
  const role = (live ?? (tone === 'error' ? 'alert' : 'status')) || undefined;
  const retry = onRetry ? (
    <Tooltip label={retryTip}>
      <Button variant="tonal" size={variant === 'banner' ? 'md' : 'sm'} onClick={onRetry}>{retryLabel}</Button>
    </Tooltip>
  ) : null;
  const dismiss = onDismiss ? (
    <Tooltip label={dismissLabel}>
      <IconButton label={dismissLabel} size="sm" onClick={onDismiss} className={variant === 'banner' ? `-mr-2 ${action || retry ? 'mt-0.5' : '-my-1'}` : '-my-1 flex-none'}>
        <IconClose />
      </IconButton>
    </Tooltip>
  ) : null;
  const actions = action || retry ? <>{action}{retry}</> : null;

  let box: React.ReactElement;
  if (variant === 'text') {
    box = (
      <div role={role} data-tone={tone} className={`flex min-w-0 items-start gap-1.5 text-sm leading-5 ${t.ink} ${className}`}>
        {icon !== false && <span className="mt-0.5 flex flex-none">{icon || <t.Icon className="h-4 w-4" />}</span>}
        <div className="min-w-0">
          {title && <span className="font-semibold">{title} </span>}
          {children}
        </div>
        {actions && <div className="-my-1 flex flex-none items-center gap-2">{actions}</div>}
        {dismiss}
      </div>
    );
  } else if (variant === 'note') {
    box = (
      <div role={role} data-tone={tone} className={`inline-flex min-w-0 max-w-full items-start gap-1.5 rounded-control px-2 py-1 text-xs leading-4 text-on-surface ${t.wash} ${className}`}>
        {icon !== false && <span className={`flex flex-none ${t.ink}`}>{icon || <t.Icon className="h-4 w-4" />}</span>}
        <span className="min-w-0">
          {title && <span className="font-semibold">{title} </span>}
          {children}
        </span>
        {actions && <span className="-my-1 ml-1 flex flex-none items-center gap-1">{actions}</span>}
        {dismiss}
      </div>
    );
  } else {
    box = (
      <div role={role} data-tone={tone} className={`flex min-w-0 items-start gap-3 rounded-panel px-4 py-3 text-sm leading-5 ${t.wash} ${className}`}>
        {icon !== false && (
          <span className={`flex flex-none ${actions ? 'mt-1.5' : ''} ${t.ink}`}>{icon || <t.Icon className="h-5 w-5" />}</span>
        )}
        <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-4 gap-y-2">
          {/* Padded to the keys' height so a one-line message centres on them; a long one starts level with them. */}
          <div className={`min-w-0 flex-1 basis-56 ${actions ? 'py-1.5' : ''}`}>
            {title && <p className="m-0 text-balance font-semibold text-on-surface">{title}</p>}
            {children && <div className={title ? 'mt-0.5 text-on-surface-variant' : 'text-on-surface'}>{children}</div>}
          </div>
          {actions && <div className="flex flex-none flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {dismiss}
      </div>
    );
  }

  return show === undefined ? box : <Reveal show={show}>{box}</Reveal>;
};

export default Alert;
